<template>
  <!-- Table Container Card -->
  <b-card
    no-body
  >
    <div class="m-2">

      <!-- Table Top -->
      <b-row>

        <!-- Per Page -->
        <b-col
          cols="12"
          md="6"
          class="d-flex align-items-center justify-content-start mb-1 mb-md-0"
        >
          <TableEntries
            v-model="perPage"
            :options="perPageOptions"
          />
          <Button
            v-if="getUserPermission('VIEW_ISSUED_CARDS')"
            :label="$t('Issue Card')"
            size="md"
            @click="gotoIssueCard"
          />
        </b-col>

        <!-- Search -->
        <b-col
          cols="12"
          md="6"
        >
          <div class="d-flex align-items-center justify-content-lg-end">
            <Button
              :label="$t('Filter')"
              size="md"
              icon="FilterIcon"
              show-icon
              class="mr-1"
              @click="showModal('modal-filter')"
            />
            <Button
              :label="$t('Reset Filter')"
              size="md"
              icon="RotateCwIcon"
              show-icon
              @click="resetFilter"
            />
          </div>
        </b-col>
      </b-row>
    </div>
    <b-table
      ref="cardProfiles"
      :busy="isBusy"
      :per-page="pageSize"
      :current-page="currentPage"
      :items="cardAccountList"
      responsive
      :fields="translatedTableHeaders"
      primary-key="id"
      show-empty
      :empty-text="$t('No records available')"
      class="position-relative"
    >
      <template #table-busy>
        <div class="text-center text-danger my-2">
          <b-spinner
            class="align-middle"
            variant="primary"
          />
        </div>
      </template>
      <template #cell(SN)="data">
        {{ getOverallIndex(data.index) }}
      </template>
      <template #cell(dateCreated)="data">
        {{ data.value ? toDateWithYear(data.value) : '' }}
      </template>
      <template #cell(cardCreationDate)="data">
        {{ data.value ? toDateWithYear(data.value) : '' }}
      </template>

      <template #cell(status)="data">
        <b-badge
          pill
          :variant="getStatusColor(data.value)"
        >
          {{ data.value === 'Active' ? $t('Linked') : data.value }}
        </b-badge>
      </template>

      <!-- Column: Actions -->
      <template #cell(actions)="data">
        <div class="text-nowrap">
          <!-- Dropdown -->
          <b-dropdown
            variant="link"
            toggle-class="p-0"
            no-caret
            :right="$store.state.appConfig.isRTL"
          >

            <template #button-content>
              <feather-icon
                icon="MoreVerticalIcon"
                size="16"
                class="align-middle text-body"
              />
            </template>
            <b-dropdown-item
              v-if="getUserPermission((data.item.status === 'Linked' || data.item.status === 'Active') ? 'UNLINK_INSTANT_CARD' : 'LINK_INSTANT_CARD')"
              @click="cardAccountStatus(data.item, $event.target)"
            >
              <feather-icon icon="CheckSquareIcon" />
              <span class="align-middle ml-50">{{ (data.item.status === 'Linked' || data.item.status === 'Active') ? $t('Unlink Card') : $t('Link Card') }}</span>
            </b-dropdown-item>
            <b-dropdown-item
              v-if="false && getUserPermission('VIEW_ISSUED_CARDS') && data.item.cardCreationDate"
              @click="setupCardLimit(data.item, $event.target)"
            >
              <feather-icon icon="MinusSquareIcon" />
              <span class="align-middle ml-50"> {{ $t('Card Limit') }} </span>
            </b-dropdown-item>
            <b-dropdown-item
              v-if="getUserPermission('VIEW_ISSUED_CARDS') && !data.item.isPinSet"
              @click="setupGeneratePin(data.item, $event.target)"
            >
              <feather-icon icon="LockIcon" />
              <span class="align-middle ml-50"> {{ $t('Generate PIN') }} </span>
            </b-dropdown-item>
          </b-dropdown>
        </div>
      </template>

    </b-table>

    <TablePagination
      v-model="currentPage"
      :from="from"
      :to="to"
      :total-rows="totalEntries"
      :per-page="perPage"
    />

    <!-- Filter modal -->
    <b-modal
      id="modal-filter"
      ref="my-modal"
      :ok-title="$t('Apply')"
      no-close-on-backdrop
      :cancel-title="$t('Cancel')"
      cancel-variant="outline-secondary"
      centered
      size="lg"
      :title="$t('Filters')"
      modal-footer
      @ok="fetchCardAccounts"
      @cancel="resetFilters"
    >
      <b-row class="match-height">
        <!--  Account Number -->
        <b-col md="6">
          <InputField
            id="account-number"
            v-model="filter.accountNumber"
            :label="$t('Account Number')"
            :placeholder="$t('Enter Account Number')"
            type="number"
          />
        </b-col>
        <!--  Account Number -->

        <!-- Masked PAN-->
        <b-col md="6">
          <InputField
            id="masked-pan"
            v-model="filter.maskedPAN"
            :label="$t('Masked PAN')"
            :placeholder="$t('Enter Masked PAN')"
            type="text"
          />
        </b-col>
        <!-- Masked PAN-->
      </b-row>
    </b-modal>
    <!-- Filter modal -->
    <b-modal
      id="manage-cards-limit-modal"
      ref="manage-cards-limit-modal"
      :cancel-title="$t('Cancel')"
      cancel-variant="outline-secondary"
      centered
      no-close-on-backdrop
      size="lg"
      :title="$t('Customer Card Limit')"
      ok-only
    >
      <validation-observer ref="cardLimitRules">
        <b-form @submit.prevent="saveCustomerCardLimit">
          <app-collapse
            class="business-items mb-4"
            type="margin"
            accordion
          >
            <app-collapse-item
              :title="$t('ATM TRANSACTION LIMIT')"
              class="card-item"
              is-visible
            >

              <div class="d-flex">
                <h6 class="section-label mb-2 mt-0 sub-menu-title">
                  {{ $t('INFORMATION') }}
                </h6>
                <feather-icon
                  icon="InfoIcon"
                  size="16"
                  class="align-middle text-body ml-1"
                  @click="onViewATMTransactionLimitInfo"
                />
              </div>
              <b-row>
                <b-col md="6">
                  <ValidationObserver>
                    <InputNumberField
                      id="limit-per-transaction"
                      v-model.number="cardLimitFormData.atM_Single_Withdrawal_Limit"
                      :label="`${$t('Limit Per Transaction - (Institution limit:')} ${toBalance(institutionData.atM_Single_Withdrawal_Limit * 100)})`"
                      :placeholder="$t('Enter Limit Per Transaction')"
                      :error-name="$t('Limit Per Transaction')"
                      :rules="`required|limit-amount:${institutionData.atM_Single_Withdrawal_Limit}|positive`"
                      :readonly="isReadonly"
                      @input="setCardLimit"
                    />
                  </ValidationObserver>
                </b-col>
                <b-col md="6">
                  <ValidationObserver>
                    <InputField
                      id="daily-transaction-count"
                      v-model="cardLimitFormData.atM_Daily_Withdrawal_Count"
                      :label="`${$t('Daily Transaction Count  - (Institution limit:')} ${institutionData.atM_Daily_Withdrawal_Count})`"
                      :placeholder="$t('Enter Daily Transaction Count')"
                      type="number"
                      :error-name="$t('Daily Transaction Count')"
                      :rules="`required|limit-amount:${institutionData.atM_Daily_Withdrawal_Count}|positive`"
                      :readonly="isReadonly"
                      @input="setCardCount"
                    />
                  </ValidationObserver>
                </b-col>

                <b-col md="6">
                  <ValidationObserver>
                    <InputNumberField
                      id="daily-transaction-limit"
                      v-model="cardLimitFormData.atM_Daily_Withdrawal_Limit"
                      :label="`${$t('Daily Transaction limit - (Institution limit:')} ${toBalance(institutionData.atM_Daily_Withdrawal_Limit * 100)})`"
                      :placeholder="$t('Daily Transaction limit')"
                      :error-name="$t('Daily Transaction limit')"
                      :rules="`required|limit-amount:${institutionData.atM_Daily_Withdrawal_Limit}|positive`"
                      readonly
                    />
                  </ValidationObserver>
                </b-col>
                <b-col md="6">
                  <ValidationObserver>
                    <InputNumberField
                      id="weekly-transaction-limit"
                      v-model="cardLimitFormData.atM_Weekly_Withdrawal_Limit"
                      :label="`${$t('Weekly Transaction limit - (Institution limit:')} ${toBalance(institutionData.atM_Weekly_Withdrawal_Limit * 100)})`"
                      :placeholder="$t('Weekly Transaction limit')"
                      :error-name="$t('Weekly Transaction limit')"
                      :rules="`required|limit-amount:${institutionData.atM_Weekly_Withdrawal_Limit}|positive`"
                      :readonly="isReadonly"
                    />
                  </ValidationObserver>
                </b-col>
              </b-row>
            </app-collapse-item>
          </app-collapse>

          <app-collapse
            class="business-items mb-4"
            type="margin"
            accordion
          >
            <app-collapse-item
              :title="$t('POS TRANSACTION LIMIT')"
              class="card-item"
              is-visible
            >
              <div class="d-flex">
                <h6 class="section-label mb-2 mt-0 sub-menu-title">
                  {{ $t('INFORMATION') }}
                </h6>
                <feather-icon
                  icon="InfoIcon"
                  size="16"
                  class="align-middle text-body ml-1"
                  @click="onViewPOSTransactionLimitInfo"
                />
              </div>
              <b-row>
                <b-col md="6">
                  <ValidationObserver>
                    <InputNumberField
                      id="limit-per-transaction"
                      v-model.number="cardLimitFormData.poS_Daily_Transaction_Limit"
                      :label="`${$t('Limit Per Transaction - (Institution limit:')} ${toBalance(institutionData.poS_Daily_Transaction_Limit * 100)})`"
                      :placeholder="$t('Enter Limit Per Transaction')"
                      :error-name="$t('Limit Per Transaction')"
                      :rules="`required|limit-amount:${institutionData.poS_Daily_Transaction_Limit}|positive`"
                      :readonly="isReadonly"
                      @input="setPOSCardLimit"
                    />
                  </ValidationObserver>
                </b-col>
                <b-col md="6">
                  <ValidationObserver>
                    <InputField
                      id="daily-transaction-count"
                      v-model="cardLimitFormData.poS_Daily_Transaction_Count"
                      :label="`${$t('Daily Transaction Count  - (Institution limit:')} ${institutionData.poS_Daily_Transaction_Count})`"
                      :placeholder="$t('Enter Daily Transaction Count')"
                      type="number"
                      :error-name="$t('Daily Transaction Count')"
                      :rules="`limit-amount:${institutionData.poS_Daily_Transaction_Count}|positive`"
                      :readonly="isReadonly"
                      @input="setPOSCardCount"
                    />
                  </ValidationObserver>
                </b-col>
                <b-col md="6">
                  <ValidationObserver>
                    <InputNumberField
                      id="daily-transaction-limit"
                      v-model="cardLimitFormData.poS_Daily_Withdrawal_Limit"
                      :label="`${$t('Daily Transaction limit - (Institution limit:')} ${toBalance(institutionData.poS_Daily_Withdrawal_Limit * 100)})`"
                      :placeholder="$t('Daily Transaction limit')"
                      :error-name="$t('Daily Transaction limit')"
                      :rules="`required|limit-amount:${institutionData.poS_Daily_Withdrawal_Limit}|positive`"
                      readonly
                    />
                  </ValidationObserver>
                </b-col>
                <b-col md="6">
                  <ValidationObserver>
                    <InputNumberField
                      id="weekly-transaction-limit"
                      v-model="cardLimitFormData.poS_Weekly_Withdrawal_Limit"
                      :label="`${$t('Weekly Transaction limit - (Institution limit:')} ${toBalance(institutionData.poS_Weekly_Withdrawal_Limit * 100)})`"
                      :placeholder="$t('Weekly Transaction limit')"
                      :error-name="$t('Weekly Transaction limit')"
                      :rules="`required|limit-amount:${institutionData.poS_Weekly_Withdrawal_Limit}|positive`"
                      :readonly="isReadonly"
                    />
                  </ValidationObserver>
                </b-col>
              </b-row>
            </app-collapse-item>
          </app-collapse>

          <app-collapse
            class="business-items mb-4"
            type="margin"
            accordion
          >
            <app-collapse-item
              :title="$t('ONLINE TRANSACTION LIMIT')"
              class="card-item"
              is-visible
            >
              <div class="d-flex">
                <h6 class="section-label mb-2 mt-0 sub-menu-title">
                  {{ $t('INFORMATION') }}
                </h6>
                <feather-icon
                  icon="InfoIcon"
                  size="16"
                  class="align-middle text-body ml-1"
                  @click="onViewONLINETransactionLimitInfo"
                />
              </div>
              <b-row>
                <b-col md="6">
                  <ValidationObserver>
                    <InputNumberField
                      id="daily-transaction-limit"
                      v-model.number="cardLimitFormData.online_Daily_Transaction_Limit"
                      :label="`${$t('Daily Transaction limit - (Institution limit:')} ${toBalance(institutionData.online_Daily_Transaction_Limit * 100)})`"
                      :placeholder="$t('Enter Daily Transaction Limit')"
                      :error-name="$t('Daily Transaction Limit')"
                      :rules="`required|limit-amount:${institutionData.online_Daily_Transaction_Limit}|positive`"
                      :readonly="isReadonly"
                    />
                  </ValidationObserver>
                </b-col>
                <b-col md="6">
                  <ValidationObserver>
                    <InputField
                      id="daily-transaction-count"
                      v-model="cardLimitFormData.online_Daily_Transaction_Count"
                      :label="`${$t('Daily Transaction Count  - (Institution limit:')} ${institutionData.online_Daily_Transaction_Count})`"
                      :placeholder="$t('Enter Daily Transaction Count')"
                      type="number"
                      :error-name="$t('Daily Transaction Count')"
                      :rules="`limit-amount:${institutionData.online_Daily_Transaction_Count}|positive`"
                      :readonly="isReadonly"
                    />
                  </ValidationObserver>
                </b-col>
              </b-row>
            </app-collapse-item>
          </app-collapse>

          <div class="button-center mt-5">
            <div>
              <Button
                :label="buttonLabel"
                :loading="isLoading"
                :disabled="isDisabled"
                size="lg"
                @click="saveCustomerCardLimit"
              />
            </div>
          </div>
        </b-form>
      </validation-observer>
      <template
        #modal-footer="{ }"
      >
        <div />
      </template>
    </b-modal>
    <b-modal
      id="atm-transaction-limit"
      ref="atmTransactionLimitModal"
      centered
      size="md"
      ok-only
      :ok-title="$t('Okay')"
      @ok="hideModal('atm-transaction-limit')"
    >
      <div class="p-0">
        <p class="section-label mb-2">
          {{ $t('NOTE!') }}
        </p>
        <p> {{ $t('This is the limits the system would allow your customers to configure for physical ATM card transactions.') }} </p>
      </div>
    </b-modal>
    <b-modal
      id="pos-transaction-limit"
      ref="posTransactionLimit"
      centered
      size="md"
      ok-only
      ok-title="Okay"
      @ok="hideModal('pos-transaction-limit')"
    >
      <div class="p-0">
        <p class="section-label mb-2">
          {{ $t('NOTE!') }}
        </p>
        <p> {{ $t('This is the limits the system would allow your customers to configure for POS card transactions.') }} </p>
      </div>
    </b-modal>
    <b-modal
      id="online-transaction-limit"
      ref="OnlineTransactionLimit"
      centered
      size="md"
      ok-only
      ok-title="Okay"
      @ok="hideModal('online-transaction-limit')"
    >
      <div class="p-0">
        <p class="section-label mb-2">
          {{ $t('NOTE!') }}
        </p>
        <p> {{ $t('This is the limits the system would allow your customers to configure for Web/ Online card transaction.') }} </p>
      </div>
    </b-modal>
    <b-modal
      id="generate-pin"
      ref="generatePinModal"
      centered
      size="md"
      ok-only
      :ok-title="$t('Generate Pin')"
      @ok="generateAndShowPin"
    >
      <div class="p-0">
        <p class="section-label mb-2">
          {{ $t('NOTE!') }}
        </p>
        <p> {{ $t('You are required to keep the PIN generated safe, as this action can not be done twice') }} </p>
      </div>
    </b-modal>
    <b-modal
      id="pin-display-modal"
      ref="PinDisplayModal"
      centered
      size="md"
      ok-only
      :ok-title="$t('Close')"
      @ok="hideModal('pin-display-modal'); fetchCardAccounts()"
    >
      <div class="p-0">
        <p class="section-label mb-3">
          {{ $t('Generated PIN') }}
        </p>
        <div class="pin-container mb-3">
          <div class="pin-display d-flex align-items-center justify-content-between">
            <span class="pin-code">{{ generatedPin }}</span>
            <feather-icon
              icon="CopyIcon"
              class="cursor-pointer ml-1"
              @click="onCopyItem(generatedPin)"
            />
          </div>
        </div>
        <p class=" base mb-0">
          {{ $t('Please save this PIN securely. It will be required for future transactions.') }}
        </p>
      </div>
    </b-modal>
    <FullScreenSpinner :show="showSpinner" />
  </b-card>
</template>

<script>
import {
  BCard, BRow, BCol, BTable, BSpinner, BBadge, BModal, BDropdown, BDropdownItem, BForm,
} from 'bootstrap-vue'
import { ValidationObserver } from 'vee-validate'
import { mapActions } from 'vuex'
import { required } from '@validations'
import { modalMixins } from '@core/mixins/modal'
import { tableMixins } from '@core/mixins/table'
import { sweetAlert } from '@core/mixins/sweetAlert'
import Ripple from 'vue-ripple-directive'
import { heightFade } from '@core/directives/animations'
import { permissionMixin } from '@core/mixins/permission'
import AppCollapse from '@core/components/app-collapse/AppCollapse.vue'
import AppCollapseItem from '@core/components/app-collapse/AppCollapseItem.vue'
import { toBalance, toKobo, formatAmount } from '@/helpers/formatAmount'
import TablePagination from '@/views/components/table/TablePagination.vue'
import TableEntries from '@/views/components/table/TableEntries.vue'
import FullScreenSpinner from '@/views/components/spinner/FullScreenSpinner.vue'
import InputField from '../../../../forms/InputField.vue'
import InputNumberField from '../../../../forms/InputNumberField.vue'
import { gender } from '../../../../../helpers/data'
import getStatusColor from '../../../../../helpers/getStatusColor'
import Button from '../../../../components/button/Button.vue'
import { toDateWithYear } from '../../../../../helpers/formatDate'
import { eChannelsPermission } from '../../../../../helpers/permissions'

const statusOptions = [
  { label: 'Linked', value: 'Linked' },
  { label: 'Unlinked', value: 'Unlinked' },
]

export default {
  components: {
    BCard,
    BRow,
    BCol,
    BTable,
    BDropdown,
    BDropdownItem,
    BBadge,
    BForm,
    TablePagination,
    BModal,
    BSpinner,
    ValidationObserver,
    TableEntries,
    InputField,
    InputNumberField,
    Button,
    FullScreenSpinner,
    AppCollapse,
    AppCollapseItem,
  },
  directives: {
    Ripple,
    'height-fade': heightFade,
  },
  mixins: [modalMixins, tableMixins, sweetAlert, permissionMixin],
  data() {
    return {
      toDateWithYear,
      showSpinner: false,
      isDisabled: false,
      isReadonly: false,
      toBalance,
      channelType: 'ATM',
      sortBy: 'name',
      required,
      modalTitle: this.$t('Card Profile Setup'),
      sortDesc: false,
      tableHeaders: [
        { key: 'SN', label: 'S/N' },
        { key: 'accountNumber', sortable: true, label: 'Account Number' },
        { key: 'nameOnCard', sortable: true, label: 'Account Name' },
        { key: 'maskedPAN', sortable: true, label: 'Masked PAN' },
        { key: 'dateCreated', sortable: true, label: 'Card Request Date' },
        { key: 'cardCreationDate', sortable: true, label: 'Card Issue Date' },
        { key: 'cardExpiration', sortable: true, label: 'Card Expiration Date' },
        { key: 'status', sortable: true, label: 'Status' },
        { key: 'actions', label: 'Actions' },
      ],
      genderOptions: gender,
      getStatusColor,
      buttonLabel: this.$t('Save'),
      filter: {
        status: '',
        thirdPartyProvider: 2,
      },
      useCardId: '',
      cardAccountList: [],
      cardLimitFormData: {
        cardId: '',
        atM_Single_Withdrawal_Limit: 0,
        atM_Daily_Withdrawal_Count: 0,
        atM_Daily_Withdrawal_Limit: 0,
        atM_Weekly_Withdrawal_Limit: 0,
        poS_Daily_Transaction_Limit: 0,
        poS_Daily_Transaction_Count: 0,
        poS_Daily_Withdrawal_Limit: 0,
        poS_Weekly_Withdrawal_Limit: 0,
        online_Daily_Transaction_Limit: 0,
        online_Daily_Transaction_Count: 0,
      },
      institutionData: {
        atM_Daily_Withdrawal_Limit: 0,
        atM_Daily_Withdrawal_Count: 0,
        atM_Single_Withdrawal_Limit: 0,
        atM_Weekly_Withdrawal_Limit: 0,
        poS_Daily_Withdrawal_Limit: 0,
        poS_Daily_Transaction_Count: 0,
        poS_Daily_Transaction_Limit: 0,
        poS_Weekly_Withdrawal_Limit: 0,
        online_Daily_Transaction_Limit: 0,
        online_Daily_Transaction_Count: 0,
        default_Customer_ATM_Daily_Withdrawal_Count: 0,
        default_Customer_ATM_Daily_Withdrawal_Limit: 0,
        default_Customer_ATM_Single_Withdrawal_Limit: 0,
        default_Customer_ATM_Weekly_Withdrawal_Limit: 0,
        default_Customer_POS_Daily_Transaction_Count: 0,
        default_Customer_POS_Daily_Transaction_Limit: 0,
        default_Customer_POS_Daily_Withdrawal_Limit: 0,
        default_Customer_POS_Weekly_Withdrawal_Limit: 0,
        default_Customer_Online_Daily_Transaction_Count: 0,
        default_Customer_Online_Daily_Transaction_Limit: 0,
      },
      generatedPin: '',
    }
  },
  computed: {
    translatedTableHeaders() {
      return this.tableHeaders.map(header => ({ ...header, label: header.label ? this.$t(header.label) : header.key }))
    },
    translatedStatusOptions() {
      return statusOptions.map(header => ({ ...header, label: header.label ? this.$t(header.label) : header.value }))
    },
  },
  watch: {
    currentPage(newValue) {
      this.currentPage = newValue
      this.fetchCardAccounts()
    },
    perPage(newValue) {
      this.perPage = newValue
      this.fetchCardAccounts()
    },
  },
  mounted() {
    this.fetchCardAccounts()
  },
  methods: {
    ...mapActions({
      getProvidusCardAccountList: 'eChannels/getProvidusCardAccountList',
      updateCardAccountStatus: 'eChannels/updateCardStatus',
      getCardLimitDetails: 'eChannels/getCardLimitDetails',
      getInstitutionLimitDetails: 'eChannels/getInstitutionLimitDetails',
      createCardLimit: 'eChannels/createCardLimit',
      updateCardLimit: 'eChannels/updateCardLimit',
      downloadBatchCard: 'eChannels/downloadBatchCard',
      generatePinAction: 'eChannels/generatePinAction',
      unlinkCard: 'eChannels/unlinkCard',
    }),
    onViewATMTransactionLimitInfo() {
      this.$root.$emit('bv::show::modal', 'atm-transaction-limit')
    },
    onViewPOSTransactionLimitInfo() {
      this.$root.$emit('bv::show::modal', 'pos-transaction-limit')
    },
    onViewONLINETransactionLimitInfo() {
      this.$root.$emit('bv::show::modal', 'online-transaction-limit')
    },
    async fetchInstitutionLimitDetails() {
      try {
        this.showSpinner = true
        const response = await this.getInstitutionLimitDetails({
          permission: eChannelsPermission.VIEW_CARD_LIMIT,
        })

        const { status, data } = await response
        if (status === 200 && data) {
          this.institutionData = {
            atM_Daily_Withdrawal_Limit: formatAmount(data.atM_Daily_Withdrawal_Limit),
            atM_Weekly_Withdrawal_Limit: formatAmount(data.atM_Weekly_Withdrawal_Limit),
            atM_Daily_Withdrawal_Count: Number(data.atM_Daily_Withdrawal_Count),
            atM_Single_Withdrawal_Limit: formatAmount(data.atM_Single_Withdrawal_Limit),
            poS_Daily_Withdrawal_Limit: formatAmount(data.poS_Daily_Withdrawal_Limit),
            poS_Weekly_Withdrawal_Limit: formatAmount(data.poS_Weekly_Withdrawal_Limit),
            poS_Daily_Transaction_Count: Number(data.poS_Daily_Transaction_Count),
            poS_Daily_Transaction_Limit: formatAmount(data.poS_Daily_Transaction_Limit),
            online_Daily_Transaction_Limit: formatAmount(data.online_Daily_Transaction_Limit),
            online_Daily_Transaction_Count: Number(data.online_Daily_Transaction_Count),

            default_Customer_ATM_Daily_Withdrawal_Count: data.default_Customer_ATM_Daily_Withdrawal_Count,
            default_Customer_ATM_Daily_Withdrawal_Limit: data.default_Customer_ATM_Daily_Withdrawal_Limit,
            default_Customer_ATM_Single_Withdrawal_Limit: data.default_Customer_ATM_Single_Withdrawal_Limit,
            default_Customer_ATM_Weekly_Withdrawal_Limit: data.default_Customer_ATM_Weekly_Withdrawal_Limit,
            default_Customer_POS_Daily_Transaction_Count: data.default_Customer_POS_Daily_Transaction_Count,
            default_Customer_POS_Daily_Transaction_Limit: data.default_Customer_POS_Daily_Transaction_Limit,
            default_Customer_POS_Daily_Withdrawal_Limit: data.default_Customer_POS_Daily_Withdrawal_Limit,
            default_Customer_POS_Weekly_Withdrawal_Limit: data.default_Customer_POS_Weekly_Withdrawal_Limit,
            default_Customer_Online_Daily_Transaction_Count: data.default_Customer_Online_Daily_Transaction_Count,
            default_Customer_Online_Daily_Transaction_Limit: data.default_Customer_Online_Daily_Transaction_Limit,
          }
          this.showSpinner = false
        }
      } catch (error) {
        this.handleError(error)
      } finally {
        this.showSpinner = false
      }
    },
    async fetchCardLimitDetails() {
      try {
        this.showSpinner = true
        const response = await this.getCardLimitDetails({
          cardId: this.useCardId,
          permission: eChannelsPermission.VIEW_CARD_LIMIT,
        })
        const { status, data } = await response
        if (status === 200 && data) {
          this.cardLimitFormData = {
            cardId: this.useCardId,
            atM_Daily_Withdrawal_Limit: formatAmount(data.atM_Daily_Withdrawal_Limit),
            atM_Weekly_Withdrawal_Limit: formatAmount(data.atM_Weekly_Withdrawal_Limit),
            atM_Daily_Withdrawal_Count: data.atM_Daily_Withdrawal_Count,
            atM_Single_Withdrawal_Limit: formatAmount(data.atM_Single_Withdrawal_Limit),
            poS_Daily_Withdrawal_Limit: formatAmount(data.poS_Daily_Withdrawal_Limit),
            poS_Weekly_Withdrawal_Limit: formatAmount(data.poS_Weekly_Withdrawal_Limit),
            poS_Daily_Transaction_Count: data.poS_Daily_Transaction_Count,
            poS_Daily_Transaction_Limit: formatAmount(data.poS_Daily_Transaction_Limit),
            online_Daily_Transaction_Limit: formatAmount(data.online_Daily_Transaction_Limit),
            online_Daily_Transaction_Count: data.online_Daily_Transaction_Count,
          }
          this.buttonLabel = this.$t('Update')
          this.showSpinner = false
        } else {
          this.cardLimitFormData = {
            // Apply institution default config
            cardId: this.useCardId,
            atM_Single_Withdrawal_Limit: formatAmount(this.institutionData.default_Customer_ATM_Single_Withdrawal_Limit),
            atM_Daily_Withdrawal_Count: Number(this.institutionData.default_Customer_ATM_Daily_Withdrawal_Count),
            atM_Daily_Withdrawal_Limit: formatAmount(this.institutionData.default_Customer_ATM_Daily_Withdrawal_Limit),
            atM_Weekly_Withdrawal_Limit: formatAmount(this.institutionData.default_Customer_ATM_Weekly_Withdrawal_Limit),
            poS_Daily_Withdrawal_Limit: formatAmount(this.institutionData.default_Customer_POS_Daily_Withdrawal_Limit),
            poS_Weekly_Withdrawal_Limit: formatAmount(this.institutionData.default_Customer_POS_Weekly_Withdrawal_Limit),
            poS_Daily_Transaction_Count: Number(this.institutionData.default_Customer_POS_Daily_Transaction_Count),
            poS_Daily_Transaction_Limit: formatAmount(this.institutionData.default_Customer_POS_Daily_Transaction_Limit),
            online_Daily_Transaction_Limit: formatAmount(this.institutionData.default_Customer_Online_Daily_Transaction_Limit),
            online_Daily_Transaction_Count: Number(this.institutionData.default_Customer_Online_Daily_Transaction_Count),
          }
          this.buttonLabel = this.$t('Update')
          this.showSpinner = false
        }
      } catch (error) {
        this.handleError(error)
      } finally {
        this.showSpinner = false
      }
    },
    async setupCardLimit(item) {
      this.useCardId = item.cardId
      await this.fetchInstitutionLimitDetails()
      await this.fetchCardLimitDetails()
      this.$root.$emit('bv::show::modal', 'manage-cards-limit-modal')
    },
    async setupGeneratePin(item) {
      this.useCardId = item.cardId
      this.$root.$emit('bv::show::modal', 'generate-pin')
    },

    // Fix the showPinModal method
    async generateAndShowPin() {
      try {
        this.showSpinner = true
        const formData = {
          cardId: this.useCardId,
        }
        const response = await this.generatePinAction({
          formData,
          permission: eChannelsPermission.CREATE_DEFAULT_PIN,
        })
        if (response.status === 200) {
          this.generatedPin = response.data.pin
        }
        this.hideModal('generate-pin')
        // Then show the pin-display-modal
        this.$nextTick(() => {
          this.$root.$emit('bv::show::modal', 'pin-display-modal')
        })
      } catch (error) {
        this.alertError({ text: this.$t('Error generating PIN'), error })
      } finally {
        this.showSpinner = false
      }
    },
    // Your existing onCopyItem method is correct
    onCopyItem(item) {
      try {
        navigator.clipboard.writeText(item)
        this.alertSuccess({ text: this.$t('Copied to Clipboard') })
      } catch (error) {
        this.alertError({ text: this.$t('Error copying to clipboard:'), error })
      }
    },
    setCardLimit(value) {
      this.cardLimitFormData.atM_Daily_Withdrawal_Limit = value * this.cardLimitFormData.atM_Daily_Withdrawal_Count
    },
    setCardCount(value) {
      this.cardLimitFormData.atM_Daily_Withdrawal_Limit = this.cardLimitFormData.atM_Single_Withdrawal_Limit * value
    },
    setPOSCardLimit(value) {
      this.cardLimitFormData.poS_Daily_Withdrawal_Limit = value * this.cardLimitFormData.poS_Daily_Transaction_Count
    },
    setPOSCardCount(value) {
      this.cardLimitFormData.poS_Daily_Withdrawal_Limit = this.cardLimitFormData.poS_Daily_Transaction_Limit * value
    },
    saveCustomerCardLimit() {
      this.$refs.cardLimitRules.validate().then(success => {
        if (success) {
          const areYouSure = this.$t('Are you sure you want to')
          const thisText = this.$t('this Customer Card Limit?')
          this.confirm(this.cardLimitSubmit, { text: `${areYouSure} ${this.buttonLabel} ${thisText}` })
        }
      })
    },
    async cardLimitSubmit() {
      try {
        this.isLoading = true
        this.isDisabled = true
        const formData = {
          cardId: this.cardLimitFormData.cardId,
          atM_Daily_Withdrawal_Limit: toKobo(this.cardLimitFormData.atM_Daily_Withdrawal_Limit),
          atM_Single_Withdrawal_Limit: toKobo(this.cardLimitFormData.atM_Single_Withdrawal_Limit),
          atM_Daily_Withdrawal_Count: Number(this.cardLimitFormData.atM_Daily_Withdrawal_Count),
          atM_Weekly_Withdrawal_Limit: toKobo(this.cardLimitFormData.atM_Weekly_Withdrawal_Limit),
          poS_Daily_Transaction_Limit: toKobo(this.cardLimitFormData.poS_Daily_Transaction_Limit),
          poS_Daily_Withdrawal_Limit: toKobo(this.cardLimitFormData.poS_Daily_Withdrawal_Limit),
          poS_Daily_Transaction_Count: Number(this.cardLimitFormData.poS_Daily_Transaction_Count),
          poS_Weekly_Withdrawal_Limit: toKobo(this.cardLimitFormData.poS_Weekly_Withdrawal_Limit),
          online_Daily_Transaction_Limit: toKobo(this.cardLimitFormData.online_Daily_Transaction_Limit),
          online_Daily_Transaction_Count: Number(this.cardLimitFormData.online_Daily_Transaction_Count),
          atM_Maximum_Daily_Withdrawal_Limit: toKobo(this.cardLimitFormData.atM_Daily_Withdrawal_Limit),
          atM_Maximum_Single_Withdrawal_Limit: toKobo(this.cardLimitFormData.atM_Single_Withdrawal_Limit),
          atM_Maximum_Weekly_Withdrawal_Limit: toKobo(this.cardLimitFormData.atM_Weekly_Withdrawal_Limit),
          atM_Maximum_Daily_Withdrawal_Count: Number(this.cardLimitFormData.atM_Daily_Withdrawal_Count),
          poS_Maximum_Daily_Transaction_Limit: toKobo(this.cardLimitFormData.poS_Daily_Transaction_Limit),
          poS_Maximum_Daily_Transaction_Count: Number(this.cardLimitFormData.poS_Daily_Transaction_Count),
          poS_Maximum_Daily_Withdrawal_Limit: toKobo(this.cardLimitFormData.poS_Daily_Withdrawal_Limit),
          poS_Maximum_Weekly_Withdrawal_Limit: toKobo(this.cardLimitFormData.poS_Weekly_Withdrawal_Limit),
          online_Maximum_Daily_Transaction_Limit: toKobo(this.cardLimitFormData.online_Daily_Transaction_Limit),
          online_Maximum_Daily_Transaction_Count: Number(this.cardLimitFormData.online_Daily_Transaction_Count),
        }

        const formDataUpdate = {
          atM_Daily_Withdrawal_Limit: toKobo(this.cardLimitFormData.atM_Daily_Withdrawal_Limit),
          atM_Single_Withdrawal_Limit: toKobo(this.cardLimitFormData.atM_Single_Withdrawal_Limit),
          atM_Daily_Withdrawal_Count: Number(this.cardLimitFormData.atM_Daily_Withdrawal_Count),
          atM_Weekly_Withdrawal_Limit: toKobo(this.cardLimitFormData.atM_Weekly_Withdrawal_Limit),
          poS_Daily_Transaction_Limit: toKobo(this.cardLimitFormData.poS_Daily_Transaction_Limit),
          poS_Daily_Transaction_Count: Number(this.cardLimitFormData.poS_Daily_Transaction_Count),
          poS_Daily_Withdrawal_Limit: toKobo(this.cardLimitFormData.poS_Daily_Withdrawal_Limit),
          poS_Weekly_Withdrawal_Limit: toKobo(this.cardLimitFormData.poS_Weekly_Withdrawal_Limit),
          online_Daily_Transaction_Limit: toKobo(this.cardLimitFormData.online_Daily_Transaction_Limit),
          online_Daily_Transaction_Count: Number(this.cardLimitFormData.online_Daily_Transaction_Count),
          cardId: this.cardLimitFormData.cardId,
        }
        const response = this.buttonLabel === 'Save'
          ? await this.createCardLimit({
            formData, permission: eChannelsPermission.CREATE_CARD_LIMIT,
          })
          : await this.updateCardLimit({
            formDataUpdate,
            permission: eChannelsPermission.UPDATE_CARD_LIMIT,
          })

        const { status } = await response
        if (status === 200) {
          const text1 = this.$t('Your request to')
          const text2 = this.$t('Customer Card Limit was successful')
          this.alertSuccess({ text: `${text1} ${this.buttonLabel} ${text2}` })
          this.hideModal('manage-cards-limit-modal')
        }
      } catch (error) {
        this.handleError(error)
      } finally {
        this.isLoading = false
        this.isDisabled = false
      }
    },
    async fetchCardAccounts() {
      try {
        this.isBusy = true
        this.hideModal('modal-filter')
        const response = await this.getProvidusCardAccountList({
          search: this.filter,
          page: this.currentPage,
          limit: this.perPage,
          permission: eChannelsPermission.VIEW_ISSUED_CARDS,
        })
        const { status, data } = response
        if (status === 200 && data.data) {
          // Flatten card fields for table display but keep original card object
          this.cardAccountList = data.data.map(items => ({
            card: items.card,
            ...items.card,
            ...items,
          }))
          this.totalCount = data.recordCount
        } else {
          this.cardAccountList = []
          this.totalCount = 0
        }
      } catch (error) {
        this.handleError(error)
      } finally {
        this.isBusy = false
      }
    },
    cardAccountStatus(items) {
      // Safely determine the id to send to the UpdateStatus API
      const candidateCard = items.card || {}
      const statusId = candidateCard.id || items.cardId || items.id

      if (!statusId) {
        this.alertError({ text: this.$t('Unable to locate card identifier for this record') })
        return
      }

      const isLinked = items.status === 'Linked' || items.status === 'Active'
      const action = isLinked ? this.$t('Unlink') : this.$t('Link')
      const statusType = isLinked ? 1 : 2
      const permission = isLinked ? eChannelsPermission.UNLINK_INSTANT_CARD : eChannelsPermission.LINK_INSTANT_CARD

      const updateCardStatus = async () => {
        try {
          this.showSpinner = true
          let response

          if (isLinked) {
            const formData = {
              cardId: statusId,
            }
            response = await this.unlinkCard({
              formData,
              permission,
            })
          } else {
            const statusValues = {
              id: statusId,
              status: statusType,
            }
            response = await this.updateCardAccountStatus({
              statusValues,
              permission,
            })
          }
          if (response.status === 200) {
            this.fetchCardAccounts()
            const requestText = this.$t('Your request to')
            const cardText = this.$t('Card was successful')
            this.alertSuccess({ text: `${requestText} ${action} ${cardText}` })
            this.showSpinner = false
          }
        } catch (error) {
          this.handleError(error)
        } finally {
          this.showSpinner = false
        }
      }
      const areYouSure = this.$t('Are you sure you want to')
      const thisText = this.$t('this Card?')
      this.confirm(updateCardStatus, { title: '', text: `${areYouSure} ${action} ${thisText}` })
    },
    gotoIssueCard() {
      this.$router.push({
        name: 'providus-issue-card',
        params: {
          type: 'add',
        },
      }).catch(error => {
        this.alertError({ text: error })
      })
    },
    goToCardAccountDetails(item, activity, val) {
      this.$router.push({
        name: 'providus-issue-card',
        params: {
          type: val,
          item,
        },
      }).catch(error => {
        this.alertError({ text: error })
      })
    },
    resetFilters() {
      this.filter = { ...null }
    },
    resetFilter() {
      this.filter = { ...null }
      this.fetchCardAccounts()
    },
  },
}
</script>

<style scoped>
.button-center {
  display: flex;
  justify-content: center;
  align-items: center;
}
.pin-code {
  font-size: 2rem;
  font-weight: 900;
  letter-spacing: 0.3em;
  color: #2c3e50;
}

.pin-container {
  background-color: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 0.375rem;
  padding: 1.5rem;
}

.pin-display {
  background-color: white;
  border: 2px solid #007bff;
  border-radius: 0.25rem;
  padding: 1.25rem 1.5rem;
}
</style>
